/**
 * 战斗系统
 * 回合制自动战斗
 */

import type { PlayerState } from './Player';
import { getCombatStoneMultiplier, getPlayerOrigin, getMaxInventorySize } from './Player';
import type { Enemy, EnemyDrop } from '../data/enemies';
import { getRandomEnemy, type Enemy as EnemyType } from '../data/enemies';
import { findRealmLevel } from '../data/realms';
import { REALMS } from '../data/realms';
import { ITEMS } from '../data/items';
import type { Zone } from '../data/zones';

export interface CombatState {
  player: PlayerState;
  enemy: Enemy;
  enemyMaxHp: number;
  isOver: boolean;
  playerWon: boolean;
  playerFled: boolean;
  log: string[];
  turn: number;
  zone?: Zone;
}

export function initiateCombat(player: PlayerState, zone?: Zone): CombatState {
  const realmLevel = findRealmLevel(player.realmKey);
  const realmIndex = realmLevel
    ? REALMS.findIndex((r) => r.levels.some((l) => l.key === player.realmKey))
    : 0;

  const enemy = getRandomEnemy(Math.max(0, realmIndex));
  const powerMult = zone?.enemyPower ?? 1.0;
  const scaledEnemy: Enemy = {
    ...enemy,
    hp: Math.floor(enemy.hp * powerMult),
    attack: Math.floor(enemy.attack * powerMult),
    defense: Math.floor(enemy.defense * powerMult),
  };

  const zoneName = zone ? `【${zone.icon} ${zone.name}】` : '';
  return {
    player,
    enemy: scaledEnemy,
    enemyMaxHp: scaledEnemy.hp,
    isOver: false,
    playerWon: false,
    log: [`⚔️ ${zoneName}遭遇了 ${enemy.name}！战斗开始！`],
    turn: 0,
    playerFled: false,
    zone,
  };
}

export function executeCombatRound(state: CombatState): CombatState {
  if (state.isOver) return state;
  state.turn++;

  const playerDamage = calcDamage(state.player.attack, state.enemy.defense);
  state.enemy.hp -= playerDamage;
  state.log.push(`🗡️ 第${state.turn}回合：你攻击 ${state.enemy.name}，造成 ${playerDamage} 点伤害`);

  if (state.enemy.hp <= 0) {
    state.enemy.hp = 0;
    state.isOver = true;
    state.playerWon = true;

    const zone = state.zone;
    const expReward = Math.floor(state.enemy.expReward * (zone?.expBonus ?? 1.0));
    const stoneMult = getCombatStoneMultiplier(state.player);
    const stoneReward = Math.floor(state.enemy.stoneReward * stoneMult * (zone?.stoneBonus ?? 1.0));
    state.player.cultivationExp += expReward;
    state.player.spiritStones += stoneReward;
    state.player.killCount++;

    state.log.push(`🏆 胜利！击败了 ${state.enemy.name}！`);
    state.log.push(`💰 获得 ${expReward} 修炼经验，${stoneReward} 灵石${stoneMult > 1 ? '（身份加成）' : ''}`);
    processDrops(state);
    return state;
  }

  const enemyDamage = calcDamage(state.enemy.attack, state.player.defense);
  state.player.hp -= enemyDamage;
  state.log.push(`💢 ${state.enemy.name} 反击，对你造成 ${enemyDamage} 点伤害`);

  if (state.player.hp <= 0) {
    state.player.hp = 0;
    state.isOver = true;
    state.playerWon = false;
    state.player.deathCount++;
    state.log.push(`💀 你被 ${state.enemy.name} 击败了...`);
    state.log.push(`💀 修仙界残酷无情...`);
  }

  return state;
}

export function tryFlee(state: CombatState): { success: boolean; message: string } {
  const origin = getPlayerOrigin(state.player);
  let fleeChance = 0.4 + (origin.fleeBonus ?? 0);
  const realmLevel = findRealmLevel(state.player.realmKey);
  if (realmLevel) {
    const realmIndex = REALMS.findIndex((r) => r.levels.some((l) => l.key === state.player.realmKey));
    fleeChance += realmIndex * 0.05;
  }
  fleeChance = Math.min(0.9, fleeChance);

  if (Math.random() < fleeChance) {
    state.isOver = true;
    state.playerFled = true;
    state.log.push('🏃 你成功逃脱了！');
    return { success: true, message: '成功逃脱！' };
  } else {
    state.log.push('🏃 逃跑失败！敌人拦住了你的去路。');
    const enemyDamage = calcDamage(state.enemy.attack, state.player.defense);
    state.player.hp -= enemyDamage;
    state.log.push(`💢 ${state.enemy.name} 趁机攻击，造成 ${enemyDamage} 点伤害`);
    if (state.player.hp <= 0) {
      state.player.hp = 0;
      state.isOver = true;
      state.playerWon = false;
      state.player.deathCount++;
    }
    return { success: false, message: '逃跑失败！' };
  }
}

function processDrops(state: CombatState): void {
  const maxSize = getMaxInventorySize(state.player);
  const zone = state.zone;
  for (const drop of state.enemy.drops) {
    let effectiveChance = drop.chance;
    if (drop.itemId === 'spirit_herb') effectiveChance *= (zone?.herbBonus ?? 1.0);
    if (drop.itemId === 'monster_core') effectiveChance *= (zone?.coreBonus ?? 1.0);
    effectiveChance = Math.min(1.0, effectiveChance);
    if (Math.random() >= effectiveChance) continue;

    const count = drop.countMin + Math.floor(Math.random() * (drop.countMax - drop.countMin + 1));
    const template = ITEMS.find((i) => i.id === drop.itemId);
    for (let i = 0; i < count; i++) {
      if (state.player.inventory.length >= maxSize) {
        state.log.push(`🎒 背包已满！${drop.itemName} 无法放入`);
        break;
      }
      if (template) state.player.inventory.push(JSON.parse(JSON.stringify(template)));
    }
    state.log.push(`📦 获得 ${drop.itemName} ×${count}`);
  }
}

function calcDamage(attack: number, defense: number): number {
  const baseDamage = attack - defense * 0.5;
  const variance = 0.8 + Math.random() * 0.4;
  return Math.max(1, Math.floor(baseDamage * variance));
}

export { getRandomEnemy };
